// useCalculateLease.js – Lease/RTO Calculation Hook
import { useCallback, useEffect, useMemo, useRef } from "react";
import useCurrentLeaseStore from "stores/LeaseStore";
import useCurrentSaleStore from "stores/SaleStore";
import useCurrentDealStore from "stores/DealStore";
import shallow from "zustand/shallow";
import {
	calculateAdditionalTax,
	calculateBasePayment,
	calculateDepreciation,
	calculateMonthlyTax,
	moneyFactorFromApr,
} from "../helperFunctions/LeaseFunctions";
import {
	calcDMVFees,
	calcLicenseFee,
	calcRegistrationFee,
	calculateFinalDueDate,
	calculateFirstDueDate,
	convertToDollar,
} from "../helperFunctions/SaleFunctions";
import useCurrentRecapStore from "../../../stores/RecapStore";
import {
	commissionSettingsEnum,
	commTypeList,
	DealStatus,
	LoanPeriod,
	profitSettingsEnum,
} from "../../../constants/Constants";

const useCalculateLease = () => {
	const roundUp = (num, digits = 2) => {
		const factor = Math.pow(10, digits);
		return Math.round(num * factor) / factor;
	};
	const deal = useCurrentDealStore((state) => state, shallow);
	const vehicle = useCurrentDealStore((state) => state?.vehicle, shallow);
	const sale = useCurrentSaleStore((state) => state, shallow);
	const lease = useCurrentLeaseStore((state) => state, shallow);
	const recap = useCurrentRecapStore((state) => state, shallow);
	const {
		dmvTaxRate,
		servContractTaxRate,
		aftMktTaxRate,
		gapTaxRate,
		docFeeTaxRate,
		dealerSmogTaxRate,
		taxCreditRate,
		taxCreditFixAmount,
		maxTaxCredit,
		vitRate,
		businessTaxRate,
		fetTaxRate,
		bodyInjuryPremium,
		collisionPremium,
		compPremium,
		medicalPremium,
		otherPremium,
		propertyPremium,
	} = useCurrentDealStore(
		(state) => ({
			dmvTaxRate: state?.deal?.dealSettings?.taxDefaults?.dmvTaxRate,
			servContractTaxRate:
				state?.deal?.dealSettings?.taxDefaults?.servContractTaxRate,
			aftMktTaxRate: state?.deal?.dealSettings?.taxDefaults?.aftMktTaxRate,
			gapTaxRate: state?.deal?.dealSettings?.taxDefaults?.gapTaxRate,
			docFeeTaxRate: state?.deal?.dealSettings?.taxDefaults?.docFeeTaxRate,
			dealerSmogTaxRate:
				state?.deal?.dealSettings?.taxDefaults?.dealerSmogTaxRate,
			taxCreditRate: state?.deal?.dealSettings?.taxDefaults?.taxCreditRate,
			taxCreditFixAmount:
				state?.deal?.dealSettings?.taxDefaults?.taxCreditFixAmount,
			maxTaxCredit: state?.deal?.dealSettings?.taxDefaults?.maxTaxCredit,

			vitRate: state?.deal?.dealSettings?.taxDefaults?.vitRate,
			businessTaxRate: state?.deal?.dealSettings?.taxDefaults?.businessTaxRate,
			fetTaxRate: state?.deal?.dealSettings?.taxDefaults?.fetTaxRate,

			bodyInjuryPremium: state.collisionInsurance?.bodyInjuryPremium,
			collisionPremium: state.collisionInsurance?.collisionPremium,
			compPremium: state.collisionInsurance?.compPremium,
			medicalPremium: state.collisionInsurance?.medicalPremium,
			otherPremium: state.collisionInsurance?.otherPremium,
			propertyPremium: state.collisionInsurance?.propertyPremium,
		}),
		shallow
	);
	const { cancellationAmt, cancellationAccepted } = useCurrentDealStore(
		(state) => state?.contractInfo,
		shallow
	);

	const dealTaxSettings = useCurrentDealStore(
		(s) => s.deal?.dealSettings?.taxDefaults
	);
	const { countyRecNum, type, isFetTaxable } = useCurrentDealStore(
		(state) => ({
			countyRecNum: state.deal.countyRecNum,
			type: state.deal.type,
			isFetTaxable: state?.vehicle?.isFetTaxable,
		}),
		shallow
	);
	// profit settings
	const {
		isDocFeeProfit,
		isDealerSmogProfit,
		isReserveProfit,
		isServConProfit,
		isGapProfit,
		isAftMktProfit,
		isMiscProfit,
		isBankFeeProfit,
	} = useCurrentDealStore(
		(state) => state?.deal?.dealSettings?.profitSettings,
		shallow
	);

	// Commission settings
	const {
		isDocFeeComm,
		isAftMktComm,
		isServConComm,
		isGapComm,
		isMiscComm,
		isBankFeeComm,
		isReserveComm,
		isSmogComm,
	} = useCurrentDealStore(
		(state) => state?.deal?.dealSettings?.commissionSettings,
		shallow
	);
	const isInHouse = () => {
		if (deal.lender?.isInHouse || false) return true;
		else return false;
	};

	const isLease = sale.saleType === 5 || sale.saleType === 6;

	// Track if this is the initial render to avoid clearing rollback on mount
	const isInitialMount = useRef(true);
	// Track previous rollBack value to detect when it's just been set
	const prevRollBack = useRef(sale.rollBack);
	// Flag to skip clearing on the immediate next effect run after rollBack is set
	const justRolled = useRef(false);

	// Auto-clear rollback when any calculation field changes
	useEffect(() => {
		console.log("=== useCalculateLease effect running ===");
		console.log("rollBack:", sale.rollBack);
		console.log("prevRollBack:", prevRollBack.current);
		console.log("justRolled:", justRolled.current);
		console.log("isInitialMount:", isInitialMount.current);

		// Skip on initial mount
		if (isInitialMount.current) {
			console.log(">>> Skipping: initial mount");
			isInitialMount.current = false;
			prevRollBack.current = sale.rollBack;
			return;
		}

		const rollBack = sale.rollBack;

		// If rollBack just changed from 0 to non-zero, it means we just did a roll
		// Set flag to skip clearing on next run
		if (prevRollBack.current === 0 && rollBack !== 0) {
			console.log(
				">>> Roll just performed, will skip auto-clear on NEXT effect run"
			);
			prevRollBack.current = rollBack;
			justRolled.current = true;
			return;
		}

		// If we just rolled, skip clearing THIS time, then clear the flag
		if (justRolled.current) {
			console.log(">>> Skipping auto-clear (roll just completed)");
			justRolled.current = false;
			prevRollBack.current = rollBack;
			return;
		}

		// If rollBack is set and fields changed, clear it (user made manual changes)
		if (rollBack !== 0 && rollBack !== null && type === DealStatus.PENDING) {
			console.log(">>> Field changed after roll, CLEARING rollback!");
			sale.editRollBack(0);
			sale.editRollPreValue(0);
		} else {
			console.log(
				">>> Not clearing rollback. rollBack:",
				rollBack,
				"type:",
				type
			);
		}

		prevRollBack.current = rollBack;
		console.log("=== useCalculateLease effect done ===\n");
	}, [
		// All lease calculation dependencies
		lease.price,
		lease.residualValue,
		lease.residualFactor,
		lease.term,
		lease.leasePeriod,
		lease.daysToPay,
		lease.lsRebate,
		lease.moneyFactor,
		lease.apr,
		lease.bankFee,
		lease.docfee,
		lease.securityDeposit,
		lease.gpsService,
		lease.mileageCharge,
		lease.capSalesTax,
		lease.capDMVFee,
		lease.capBankFee,
		lease.capGAPWaiver,
		lease.capWarranty,
		lease.capDocFee,
		lease.capMiscFee,
		lease.capOtherfee,
		lease.capDispositionFee,
		lease.capSmogFees,
		lease.capFirstPmt,
		lease.capReduction,
		lease.manufacturerRebate,
		lease.msrp,

		// Sale dependencies
		sale.loanDate,
		sale.taxRate,
		sale.down,
		sale.discount,
		sale.dealerSmog,
		sale.stateSmog,
		sale.smogImpact,
		sale.titleFee,
		sale.filingFee,
		sale.smogExemption,
		sale.weightFee,
		sale.tireFee,
		sale.bpa,
		sale.licenseFee,
		sale.licensePct,
		sale.registrationFee,
		sale.registrationPct,
		sale.reserveFlag,
		sale.fixedAmountTax,
		sale.apr,
		sale.term,
		sale.loanPeriod,

		// Recap dependencies
		recap.interestCost,
		recap.dlrParticipation,
		recap.additionalProfit,
		recap.gapInsCost,
		recap.collisionTotCost,
		recap.warrantyCost,

		// Tax defaults
		dmvTaxRate,
		servContractTaxRate,
		aftMktTaxRate,
		gapTaxRate,
		docFeeTaxRate,
		dealerSmogTaxRate,
		vitRate,
		businessTaxRate,
		fetTaxRate,
		isFetTaxable,

		// Insurance premiums
		bodyInjuryPremium,
		collisionPremium,
		compPremium,
		medicalPremium,
		otherPremium,
		propertyPremium,

		// Contract info
		cancellationAccepted,

		// Deal arrays that might change
		deal.pickupPmts,
		deal.downPayments,
		deal.miscSellerFees,
		deal.miscDMVFees,
		deal.warranty,
		deal.aftermarket,
		deal.trades,

		// Rollback tracking
		sale.rollBack,
		sale.editRollBack,
		sale.editRollPreValue,
		type,
	]);

	// let vehicleCost = vehicle?.totalCost || 0;
	// let vehicleTotalOtherCost = vehicle?.totalOtherCost || 0;
	// let vehicleTotalRecon = vehicle?.totalRecon || 0;
	// let pack = vehicle?.pack || 0;
	// let flooring = vehicle?.flooring || 0;
	// if (wholesaleInvs.length > 0) {
	// 	vehicleCost = wholesaleInvs.reduce(
	// 		(acc, inv) => acc + inv.car.totalCost,
	// 		0
	// 	);
	// 	vehicleTotalOtherCost = wholesaleInvs.reduce(
	// 		(acc, inv) => acc + inv.car.totalOtherCost,
	// 		0
	// 	);
	// 	vehicleTotalRecon = wholesaleInvs.reduce(
	// 		(acc, inv) => acc + inv.car.totalRecon,
	// 		0
	// 	);
	// 	//holdback = wholesaleInvs.reduce((acc, inv) => acc + inv.car.holdback, 0);
	// 	pack = wholesaleInvs.reduce((acc, inv) => acc + inv.car.pack, 0);
	// 	flooring = wholesaleInvs.reduce((acc, inv) => acc + inv.car.flooring, 0);
	// }

	const memoCalculateLease = useCallback(() => {
		lease.editField("leaseDate", sale.loanDate);
		if (!lease.capFirstPmt) {
			lease.editField("firstPmtDate", sale.loanDate);
			sale.editFirstDueDate(sale.loanDate);
			lease.editField("daysToPay", 0);
			sale.editDaysToPay(0);
		}
		let loanPeriod_ = sale.loanPeriod;
		if (lease?.leasePeriod === undefined)
			lease.editField("leasePeriod", loanPeriod_);
		else loanPeriod_ = lease.leasePeriod;
		let daysToPay_ = sale.daysToPay;
		if (lease?.daysToPay === undefined)
			lease.editField("daysToPay", daysToPay_);
		else daysToPay_ = lease.daysToPay;

		const firstPmtDate = calculateFirstDueDate(daysToPay_, sale.loanDate);
		const lsNextDueDate = calculateFinalDueDate(loanPeriod_, 1, firstPmtDate);
		const finalDueDate = calculateFinalDueDate(
			loanPeriod_,
			lease.term,
			firstPmtDate
		);

		// Calculate  Total Seller Fees
		const calcMiscSellerFees = () => {
			let miscFee_ = 0.0;
			let otherFee_ = 0.0;
			let nonTaxSum = 0.0;
			let sum =
				parseFloat(lease?.docfee ?? 0) +
				parseFloat(lease?.bankFee ?? 0) +
				parseFloat(lease?.securityDeposit ?? 0) +
				parseFloat(lease.gpsService ?? 0) +
				parseFloat(sale.dealerSmog ?? 0) +
				parseFloat(sale.stateSmog ?? 0);
			let sumCost = 0;
			deal.miscSellerFees.forEach((key) => {
				if (!key.isTaxable) {
					nonTaxSum += key.amount;
					otherFee_ += key.amount;
				} else {
					miscFee_ += key.amount;
				}
				sum += key.amount;
				sumCost += key.cost;
			});
			nonTaxSum = roundUp(nonTaxSum);
			sum = roundUp(sum);
			sumCost = roundUp(sumCost);
			recap.editMiscCost(sumCost);
			sale.editTotalSellerFees(sum);
			sale.editTotalNonTaxableSellerFees(nonTaxSum);
			lease.editField("miscFee", miscFee_);
			lease.editField("otherFee", otherFee_);
			return {
				miscCost_: sumCost,
				totalSellerFees_: sum,
				totalNonTaxableSellerFees_: nonTaxSum,
				totalTaxableSellerFees_: sum - nonTaxSum,
				miscFee_,
				otherFee_,
			};
		};
		const { miscCost_, totalSellerFees_, miscFee_, otherFee_ } =
			calcMiscSellerFees();
		// Calculate  Total Service Contract (Warranty)
		const calcServConTotal = () => {
			let sum = 0;
			let sumCost = 0;
			deal.warranty.forEach((key) => {
				sum += key.amount;
				sumCost += key.cost;
			});
			sum = roundUp(sum);
			sumCost = roundUp(sumCost);
			sale.editWarranty(sum);
			recap.editWarrantyCost(sumCost);
			return { warranty_: sum, warrantyCost_: sumCost };
		};
		const { warranty_, warrantyCost_ } = calcServConTotal();
		// Calculate  Total DMV Fees
		let license_ = calcLicenseFee(
			sale.licenseFee,
			sale.licensePct,
			lease.price
		);
		sale.editLicense(license_);

		let registration_ = calcRegistrationFee(
			sale.registrationFee,
			sale.registrationPct,
			lease.price
		);
		sale.editRegistration(registration_);

		const calcPropAssessmentUT = () => {
			return deal.miscDMVFees;
		};

		const miscDMVFees_ = calcPropAssessmentUT();

		let totalDMVFees_ = calcDMVFees(
			license_,
			registration_,
			sale.titleFee,
			sale.filingFee,
			sale.smogExemption,
			sale.weightFee,
			sale.tireFee,
			sale.bpa,
			miscDMVFees_
		);
		sale.editTotalDMVFees(totalDMVFees_);
		// Calculate  Total AfterMarket
		const calcAftermarket = () => {
			let sum = 0;
			let nonTaxSum = 0;
			let sumCost = 0;
			deal.aftermarket.forEach((key) => {
				if (key.isTaxable) {
					sum += key.price * key.qty;
				} else {
					nonTaxSum += key.price * key.qty;
					sum += key.price * key.qty;
				}
				sumCost += key.cost * key.qty;
			});

			sale.editAftmktCost(sumCost);
			sale.editAftmktNonTaxable(nonTaxSum);
			sale.editAftmkt(sum);

			return {
				aftmktCost_: sumCost,
				aftmktNonTaxable_: nonTaxSum,
				aftmkt_: sum,
			};
		};
		const { aftmktCost_, aftmktNonTaxable_, aftmkt_ } = calcAftermarket();
		// calculate tottal premium
		const calcTotPremium = () => {
			const sum =
				bodyInjuryPremium +
				collisionPremium +
				compPremium +
				medicalPremium +
				otherPremium +
				propertyPremium;
			sale.editCollisionTotPremium(sum);
			return { collisionTotPremium_: sum };
		};
		const { collisionTotPremium_ } = calcTotPremium();
		// Calculate Net Trade and Profit
		// double accounting for values on recap and sale
		const calcNetTradeAndProfit = () => {
			// total allowance
			let totalTrade = 0;
			let totalAcv = 0;
			let totalPayoff = 0;
			if (deal.trades.length > 0) {
				deal.trades.forEach((element) => {
					totalTrade += element.allowance;
					totalPayoff += element.payoff;
					totalAcv += element.acv;
				});
			}
			sale.editTrade(totalTrade);
			sale.editPayoff(totalPayoff);
			const totalNet = totalTrade - totalPayoff;
			sale.editTotalNetTrade(totalNet);
			recap.editTotalTradeACV(totalAcv);
			recap.editTotalTradeAllowance(totalTrade);
			recap.editTotalTradePayoff(totalPayoff);
			const profit = totalAcv - totalTrade;
			recap.editTotalTradeProfit(profit);
			return {
				trade_: totalTrade,
				payoff_: totalPayoff,
				totalNetTrade_: totalNet,
				totalTradeACV_: totalAcv,
				totalTradeProfit_: profit,
			};
		};

		const {
			trade_,
			payoff_,
			totalNetTrade_,
			totalTradeACV_,
			totalTradeProfit_,
		} = calcNetTradeAndProfit();

		// Calculate Total Pickup Payments
		const calcTotalPickupPmts = () => {
			let sum = 0;
			deal.pickupPmts.forEach((key) => {
				sum += parseFloat(key.amount);
			});
			const total = parseFloat(sum);
			sale.editTotalPickupPmts(total);
			return { totalPickupPmts_: total };
		};
		const { totalPickupPmts_ } = calcTotalPickupPmts();

		//calculate total down payments
		const calcDownPayments = () => {
			let down_ = sale.down;
			let sum = 0;
			if (deal.downPayments.length > 0) {
				deal.downPayments.forEach((key) => {
					if (key.status !== "1" && key.status !== "void") {
						sum += parseFloat(key.amtPaid);
					}
				});
				// parseFloat(sum).toFixed(2);
				sum = roundUp(sum);
				sale.editDown(sum);
				down_ = sum;
			}
			lease.editField("down", down_);
			return { down_ };
		};
		const { down_ } = calcDownPayments();
		// Calculate TAX and SalesTax
		let taxableAmount_ = 0;
		taxableAmount_ =
			parseFloat(lease.price) +
			(dmvTaxRate === -1 ? parseFloat(totalDMVFees_) : 0) +
			(servContractTaxRate === -1 ? parseFloat(warranty_) : 0) +
			(docFeeTaxRate === -1 ? parseFloat(lease.docfee) : 0) +
			(dealerSmogTaxRate === -1 ? parseFloat(sale.dealerSmog) : 0) +
			//  (gapTaxRate === -1 ? parseFloat(collisionTotPremium) : 0) +
			(gapTaxRate === -1 ? parseFloat(sale.gap) : 0) +
			(aftMktTaxRate === -1
				? parseFloat(aftmkt_) - parseFloat(aftmktNonTaxable_)
				: 0);

		deal.miscSellerFees.forEach((element) => {
			taxableAmount_ += element.isTaxable ? parseFloat(element.amount) : 0;
		});

		miscDMVFees_.forEach((element) => {
			taxableAmount_ += element.isTaxable ? parseFloat(element.amount) : 0;
		});

		let tradeTaxCredit_ = 0.0;

		tradeTaxCredit_ =
			parseFloat(trade_ || 0) * parseFloat(taxCreditRate || 0) +
			parseFloat(taxCreditFixAmount || 0);

		if (tradeTaxCredit_ > maxTaxCredit && maxTaxCredit > 0)
			tradeTaxCredit_ = maxTaxCredit;

		sale.editTradeTaxCredit(tradeTaxCredit_);
		taxableAmount_ = parseFloat(taxableAmount_) - parseFloat(tradeTaxCredit_);
		taxableAmount_ = taxableAmount_ < 0 ? 0 : taxableAmount_;
		sale.editTaxableAmount(taxableAmount_);
		/**
		 * Total amount of money that is taxable.
		 * This is has no regard for associated tax rates and is not used for
		 * any calculations, simply informative
		 **/

		let totalTaxable_ = 0.0;
		totalTaxable_ =
			parseFloat(taxableAmount_) +
			(dmvTaxRate > 0 ? parseFloat(totalDMVFees_) : 0) +
			(servContractTaxRate > 0 ? parseFloat(warranty_) : 0) +
			(docFeeTaxRate > 0 ? parseFloat(lease.docfee) : 0) +
			(dealerSmogTaxRate > 0 ? parseFloat(sale.dealerSmog) : 0) +
			(aftMktTaxRate > 0
				? parseFloat(aftmkt_) - parseFloat(aftmktNonTaxable_)
				: 0) +
			//(gapTaxRate > 0 ? parseFloat(collisionTotPremium) : 0) +
			(gapTaxRate > 0 ? parseFloat(sale.gap) : 0);
		totalTaxable_ = totalTaxable_ - lease.residualValue;
		sale.editTotalTaxable(totalTaxable_);

		const vit_ = lease.price * vitRate;
		if (isNaN(vit_)) {
			sale.editVit(0);
			sale.editVitRate(0);
		} else {
			//editVit(parseFloat(vit_.toFixed(2)));
			sale.editVit(roundUp(vit_));
			sale.editVitRate(vitRate);
		}
		const businessTax_ = taxableAmount_ * businessTaxRate;
		if (isNaN(businessTax_)) {
			sale.editBusinessTax(0);
			sale.editBusinessTaxRate(0);
		} else {
			//editBusinessTax(parseFloat(businessTax_.toFixed(2)));
			sale.editBusinessTax(roundUp(businessTax_));
			sale.editBusinessTaxRate(businessTaxRate);
		}
		const fetTax_ = sale.price * (isFetTaxable === true ? fetTaxRate : 0);
		if (isNaN(fetTax_) || fetTax_ === 0) {
			sale.editFetTax(0);
			sale.editFetTaxRate(0);
		} else {
			sale.editFetTax(roundUp(fetTax_));
			sale.editFetTaxRate(fetTaxRate);
		}
		if (isNaN(sale.taxType)) {
			sale.editTaxType(0);
		}

		//==============================================================================
		const capReduction =
			lease.lsRebate + down_ + totalNetTrade_ + totalPickupPmts_;
		const deferredTax = lease.capSalesTax ? totalPickupPmts_ * sale.taxRate : 0;
		console.log("deferredTax", deferredTax);
		lease.editField("deferredTax", deferredTax);
		const downTax = lease.capSalesTax
			? roundUp((capReduction - tradeTaxCredit_) * sale.taxRate, 2)
			: 0;
		console.log("downTax", downTax);
		const additionalTax = calculateAdditionalTax(lease, sale, dealTaxSettings);
		console.log("additionalTax", additionalTax);
		lease.editField("additionalTax", additionalTax);
		lease.editField("taxAmtInDriveOff", downTax + deferredTax + additionalTax);
		const calculateCapCost = () => {
			let capCost =
				lease.price + sale.collisionTotPremium + payoff_ + lease.mileageCharge;
			if (lease.capDMVFee) capCost += totalDMVFees_;
			if (lease.capBankFee) capCost += lease.bankFee;
			if (lease.capGAPWaiver) capCost += lease.gapWaiver;
			if (lease.capWarranty) capCost += warranty_;
			if (lease.capDocFee) capCost += lease.docfee;
			if (lease.capMiscFee) capCost += miscFee_;
			if (lease.capOtherfee) capCost += otherFee_;
			// Disposition: 0=At Lease End, 1=Drive-Off, 2=Capitalized
			if (lease.capDispositionFee === 2) capCost += lease.disposition;
			if (lease.capSmogFees)
				capCost += sale.dealerSmog + sale.stateSmog + sale.smogImpact;
			if (capCost < 0.01) capCost = 0;
			return capCost;
		};

		const capCost = calculateCapCost();
		const grossCapCost = () => {
			// if (lease.capSalesTax && !lease.capTaxOnReduction)
			// 	return capCost - downTax;
			// else return capCost;
			return capCost;
		};
		const netCap = grossCapCost() - capReduction;
		//{ saleType, totalTaxable, totalPickupPmts }
		const depreciation = calculateDepreciation(netCap, lease.residualValue);
		const monthlyDepreciation = roundUp(depreciation / lease.term, 2);
		const monthlyInterest = roundUp(
			(netCap + lease.residualValue) * lease.moneyFactor,
			2
		);
		console.log(monthlyDepreciation, monthlyInterest);
		const basePayment = calculateBasePayment(
			monthlyDepreciation,
			monthlyInterest
		);
		//const totBase = basePayment * lease.term;
		const totBase = depreciation + monthlyInterest * lease.term;
		const monthlyTax = calculateMonthlyTax(
			lease.capSalesTax,
			basePayment,
			sale.taxRate
		);
		const monthlyPmt = basePayment + monthlyTax;
		// if (depreciation != monthlyDepreciation * lease.term) {
		// 	console.log(depreciation, monthlyDepreciation * lease.term);
		// 	lease.editField(
		// 		"price",
		// 		lease.price + depreciation - monthlyDepreciation * lease.term
		// 	);
		// 	return;
		// }
		console.log(totBase, depreciation);
		let rentCharge = totBase - depreciation; //monthlyInterest * lease.term; //financeCharge
		if (rentCharge < 0.5) {
			rentCharge = 0;
		}
		const totLease = monthlyPmt * lease.term;
		const totTax = monthlyTax * lease.term;
		let totalTax = totTax + downTax + deferredTax + additionalTax;
		if (!lease.capSalesTax) {
			totalTax = convertToDollar(taxableAmount_ * sale.taxRate, 2);
		}
		/**
		 * Calculates cancellation amount and restocking fee based on the CA CCOA
		 */
		const calcCancellationAmt = () => {
			// determine cancellation amount
			let cancellationAmt_;
			let restockingFee_;
			if (lease.price <= 5000) {
				cancellationAmt_ = 75;
			} else if (lease.price <= 10000) {
				cancellationAmt_ = 150;
			} else if (lease.price <= 30000) {
				cancellationAmt_ = 250;
			} else {
				cancellationAmt_ = lease.price * 0.01;
			}

			// determine restocking fee
			if (lease.price <= 5000) {
				restockingFee_ = 175;
			} else if (lease.price <= 10000) {
				restockingFee_ = 350;
			} else {
				restockingFee_ = 500;
			}
			deal.editCancellationAmt(cancellationAmt_);
			deal.editRestockingFee(restockingFee_);
			return {
				cancellationAmt_,
				restockingFee_,
			};
		};
		const {
			cancellationAmt_,
			// restockingFee_
		} = calcCancellationAmt();

		const calculateDriveOff = () => {
			let driveOff =
				down_ +
				lease.securityDeposit +
				//lease.lsRebate +
				lease.gpsService +
				aftmkt_;
			if (!lease.capDMVFee) driveOff += totalDMVFees_;
			if (!lease.capBankFee) driveOff += lease.bankFee;
			if (!lease.capGAPWaiver) driveOff += lease.gapWaiver;
			if (!lease.capWarranty) driveOff += warranty_;
			if (!lease.capDocFee) driveOff += lease.docfee ?? 0;
			if (!lease.capMiscFee) driveOff += miscFee_;
			if (!lease.capOtherfee) driveOff += otherFee_;
			// Disposition: 0=At Lease End, 1=Drive-Off, 2=Capitalized
			if (lease.capDispositionFee === 1) driveOff += lease.disposition;
			if (!lease.capFirstPmt) driveOff += monthlyPmt;
			if (cancellationAccepted === 1)
				driveOff += parseFloat(cancellationAmt_) || 0; // + if  if

			if (!lease.capSalesTax) driveOff += totalTax;
			else driveOff += additionalTax + downTax + deferredTax;
			return driveOff;
		};

		const driveOff = calculateDriveOff();
		console.log(driveOff);
		const calcReserve = () => {
			let reserve_ = recap.reserve;
			if (sale.reserveFlag) {
				reserve_ = depreciation * recap.reservePercent;
				recap.editReserve(reserve_);
			}
			return reserve_;
		};

		const reserve_ = calcReserve();

		// recap
		const calcVehicleProfit = () => {
			const total = lease.price - (vehicle?.totalCost || 0);
			recap.editCarProfit(total);
			return total;
		};
		const carProfit_ = calcVehicleProfit();

		const calcReconProfit = () => {
			const total = vehicle?.totalOtherCost || 0 + vehicle?.totalRecon;
			recap.editRecon(total);
			recap.editReconProfit(total * -1);
			return { recon_: total, reconProfit_: total * -1 };
		};
		calcReconProfit();
		const calcOfficialFeesProfit = () => {
			const official_ = totalTax + totalDMVFees_;
			recap.editOfficial(official_);
			return official_;
		};
		// const official_ =
		calcOfficialFeesProfit();

		const calcAftermarketProfit = () => {
			let aftmktProfit_ = aftmkt_ - aftmktCost_;
			if (isAftMktProfit === profitSettingsEnum.NOT_PROFIT.value) {
				aftmktProfit_ = 0;
			}
			recap.editAftmktProfit(aftmktProfit_);
			return aftmktProfit_;
		};
		const aftmktProfit_ = calcAftermarketProfit();

		const calcMiscProfit = () => {
			let miscProfit_ =
				totalSellerFees_ -
				miscCost_ -
				(lease.docfee ?? 0) -
				lease.bankFee -
				lease.securityDeposit -
				sale.dealerSmog -
				sale.stateSmog;
			if (isMiscProfit === profitSettingsEnum.NOT_PROFIT.value) {
				miscProfit_ = 0;
			}
			if (isDocFeeProfit !== profitSettingsEnum.NOT_PROFIT.value) {
				miscProfit_ += lease.docfee ?? 0;
			}
			if (isDealerSmogProfit !== profitSettingsEnum.NOT_PROFIT.value) {
				miscProfit_ += sale.dealerSmog;
			}
			if (isBankFeeProfit !== profitSettingsEnum.NOT_PROFIT.value) {
				miscProfit_ += lease?.bankFee ?? 0;
			}
			recap.editMiscProfit(miscProfit_);
			return miscProfit_;
		};
		const miscProfit_ = calcMiscProfit();

		const calcGapProfit = () => {
			let gapInsProfit_ = sale.gap - recap.gapInsCost;
			if (isGapProfit === profitSettingsEnum.NOT_PROFIT.value) {
				gapInsProfit_ = 0;
			}
			recap.editGapInsProfit(gapInsProfit_);
			return gapInsProfit_;
		};
		const gapInsProfit_ = calcGapProfit();

		const calcCollisionTotProfit = () => {
			let collisionTotProfit_ = collisionTotPremium_ - recap.collisionTotCost;
			if (isGapProfit === profitSettingsEnum.NOT_PROFIT.value) {
				collisionTotProfit_ = 0;
			}
			recap.editCollisionTotProfit(collisionTotProfit_);
			return collisionTotProfit_;
		};
		const collisionTotProfit_ = calcCollisionTotProfit();

		const calcWarrantyProfit = () => {
			let warrantyProfit_ = warranty_ - warrantyCost_;
			if (isServConProfit === profitSettingsEnum.NOT_PROFIT.value) {
				warrantyProfit_ = 0;
			}
			recap.editWarrantyProfit(warrantyProfit_);
			return warrantyProfit_;
		};
		const warrantyProfit_ = calcWarrantyProfit();

		const calcGrossFinanProfit = () => {
			let interestNetProfit_ = 0;
			if (isInHouse()) {
				recap.editInterestProfit(rentCharge);
				recap.editInterestCost(0);
				recap.editDlrParticipation(1);
				interestNetProfit_ = rentCharge;
				recap.editInterestNetProfit(interestNetProfit_);
			} else {
				const periodPerYear = LoanPeriod[loanPeriod_].term;
				const mf = Number(
					moneyFactorFromApr(recap.interestCost, periodPerYear)
				).toFixed(8);
				const periodInterest = roundUp((netCap + lease.residualValue) * mf, 2);
				const lenderFinanceCharge = periodInterest * lease.term;

				const val = parseFloat(rentCharge) - parseFloat(lenderFinanceCharge);
				const interestProfit_ = roundUp(val);
				recap.editInterestProfit(interestProfit_);

				const participation =
					parseFloat(interestProfit_) * parseFloat(recap.dlrParticipation);
				// interestNetProfit_ = twoDecimal(
				// 	participation + parseFloat(additionalProfit)
				// );
				interestNetProfit_ = roundUp(
					participation + parseFloat(recap.additionalProfit)
				);
				recap.editInterestNetProfit(interestNetProfit_);
				// 	} else {
				// 		// cash deal
				// 		editInterestProfit(0);
				// 	}
			}
			return interestNetProfit_;
		};
		const interestNetProfit_ = calcGrossFinanProfit();

		const calcFrontend = () => {
			let frontendProfit_ = carProfit_ + totalTradeProfit_;

			if (isDocFeeProfit === profitSettingsEnum.FRONT.value) {
				frontendProfit_ += parseFloat(lease.docfee ?? 0);
			}

			if (isDealerSmogProfit === profitSettingsEnum.FRONT.value) {
				frontendProfit_ += parseFloat(sale?.dealerSmog);
			}

			if (isMiscProfit === profitSettingsEnum.FRONT.value) {
				frontendProfit_ += parseFloat(
					totalSellerFees_ -
						miscCost_ -
						(lease.docfee ?? 0) -
						lease.bankFee -
						lease.securityDeposit -
						sale.dealerSmog -
						sale.stateSmog
				);
			}

			if (isReserveProfit === profitSettingsEnum.FRONT.value) {
				frontendProfit_ += parseFloat(reserve_);
			}

			if (isServConProfit === profitSettingsEnum.FRONT.value) {
				frontendProfit_ += parseFloat(warrantyProfit_);
			}

			if (isGapProfit === profitSettingsEnum.FRONT.value) {
				frontendProfit_ += parseFloat(gapInsProfit_);
			}

			if (isAftMktProfit === profitSettingsEnum.FRONT.value) {
				frontendProfit_ += parseFloat(aftmktProfit_);
			}

			frontendProfit_ = roundUp(frontendProfit_);
			recap.editFrontendProfit(frontendProfit_);
			return frontendProfit_;
		};
		const frontendProfit_ = calcFrontend();

		const calcBackend = () => {
			let backendProfit_ = interestNetProfit_;

			if (cancellationAccepted) {
				backendProfit_ += cancellationAmt_;
			}

			if (isDocFeeProfit === profitSettingsEnum.BACK.value) {
				backendProfit_ += parseFloat(lease.docfee ?? 0);
			}
			if (isDealerSmogProfit === profitSettingsEnum.BACK.value) {
				backendProfit_ += parseFloat(sale.dealerSmog);
			}
			if (isMiscProfit === profitSettingsEnum.BACK.value) {
				backendProfit_ += parseFloat(
					totalSellerFees_ -
						miscCost_ -
						(lease.docfee ?? 0) -
						lease.bankFee -
						lease.securityDeposit -
						sale.dealerSmog -
						sale.stateSmog
				);
			}

			if (isReserveProfit === profitSettingsEnum.BACK.value) {
				backendProfit_ += parseFloat(reserve_);
			}
			if (isServConProfit === profitSettingsEnum.BACK.value) {
				backendProfit_ += parseFloat(warrantyProfit_);
			}
			if (isGapProfit === profitSettingsEnum.BACK.value) {
				backendProfit_ += parseFloat(gapInsProfit_);
				backendProfit_ += parseFloat(collisionTotProfit_);
			}
			if (isAftMktProfit === profitSettingsEnum.BACK.value) {
				backendProfit_ += parseFloat(aftmktProfit_);
			}

			backendProfit_ = roundUp(backendProfit_);
			recap.editBackendProfit(backendProfit_);
			return backendProfit_;
		};
		const backendProfit_ = calcBackend();

		const calcGPBeforeFees = () => {
			let gpBeforeFees_ =
				parseFloat(frontendProfit_) + parseFloat(backendProfit_);
			//gpBeforeFees_ = twoDecimal(gpBeforeFees_);
			gpBeforeFees_ = roundUp(gpBeforeFees_);
			recap.editGpBeforeFees(gpBeforeFees_);
			return gpBeforeFees_;
		};
		const gpBeforeFees_ = calcGPBeforeFees();
		const calcLenderFeeInfo = () => {
			const fiDiscount_ = depreciation * sale.discount;
			const totalLenderFee_ = recap.lenderFee + fiDiscount_;
			const netCheckToDealer_ =
				depreciation - totalLenderFee_ + interestNetProfit_;

			sale.editFiDiscount(fiDiscount_);
			recap.editTotalLenderFee(totalLenderFee_);
			recap.editNetCheckToDealer(netCheckToDealer_);

			return { fiDiscount_, totalLenderFee_, netCheckToDealer_ };
		};
		const {
			fiDiscount_,
			totalLenderFee_,
			// netCheckToDealer_,
		} = calcLenderFeeInfo();

		const calcGPBeforeReserve = () => {
			let gpBeforeReserve_ =
				parseFloat(gpBeforeFees_) -
				parseFloat(totalLenderFee_) +
				parseFloat(recap.incentive);
			//+				parseFloat(holdback);
			//gpBeforeReserve_ = twoDecimal(gpBeforeReserve_);
			gpBeforeReserve_ = roundUp(gpBeforeReserve_);
			recap.editGpBeforeReserve(gpBeforeReserve_);
			return gpBeforeReserve_;
		};
		const gpBeforeReserve_ = calcGPBeforeReserve();

		const calcGPBeforeComm = () => {
			let grossProfit_ = parseFloat(gpBeforeReserve_) - parseFloat(reserve_);
			//grossProfit_ = twoDecimal(grossProfit_);
			grossProfit_ = roundUp(grossProfit_);
			// why are these the same, we dont need two values
			recap.editGpBeforeComm(grossProfit_);
			recap.editGrossProfit(grossProfit_);
			return grossProfit_;
		};
		const grossProfit_ = calcGPBeforeComm();

		const calcReceivable = () => {
			let receivable_ =
				parseFloat(depreciation) -
				(parseFloat(recap.lenderFee) + parseFloat(fiDiscount_)) +
				parseFloat(interestNetProfit_) +
				parseFloat(capReduction);
			//receivable_ = twoDecimal(receivable_);
			receivable_ = roundUp(receivable_);
			recap.editReceivable(receivable_);
		};
		calcReceivable();
		///////////////////////////////////
		// commissions

		const calcSalesGross = () => {
			let salesGross_ = 0;
			salesGross_ += carProfit_;
			salesGross_ += totalTradeProfit_;
			if (
				isDocFeeComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isDocFeeComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				salesGross_ += parseFloat(lease.docfee ?? 0);
			}

			if (
				isSmogComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isSmogComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				salesGross_ += parseFloat(sale.dealerSmog);
			}

			if (
				isReserveComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isReserveComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				salesGross_ += parseFloat(reserve_);
			}

			if (
				isServConComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isServConComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				salesGross_ += parseFloat(warrantyProfit_);
			}

			if (
				isGapComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isGapComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				salesGross_ += parseFloat(gapInsProfit_);
				salesGross_ += parseFloat(collisionTotProfit_);
			}

			if (
				isAftMktComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isAftMktComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				salesGross_ += parseFloat(aftmktProfit_);
			}

			if (
				isMiscComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isMiscComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				let miscProfit__ =
					totalSellerFees_ -
					miscCost_ -
					(lease.docfee ?? 0) -
					lease.bankFee -
					lease.securityDeposit -
					sale.dealerSmog -
					sale.stateSmog;

				salesGross_ += parseFloat(miscProfit__);
			}

			if (
				isBankFeeComm === commissionSettingsEnum.FRONT_COMMISSION.value ||
				isBankFeeComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				salesGross_ += parseFloat(recap.lenderFee);
			}
			//salesGross_ = twoDecimal(salesGross_);
			salesGross_ = roundUp(salesGross_);
			recap.editSalesGross(salesGross_);
			return salesGross_;
		};

		const calcFIGross = () => {
			let fiGross_ = 0;
			fiGross_ += interestNetProfit_;

			if (
				isDocFeeComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isDocFeeComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				fiGross_ += parseFloat(lease.docfee);
			}

			if (
				isSmogComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isSmogComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				fiGross_ += parseFloat(sale.dealerSmog);
			}

			if (
				isReserveComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isReserveComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				fiGross_ += parseFloat(reserve_);
			}

			if (
				isServConComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isServConComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				fiGross_ += parseFloat(warrantyProfit_);
			}
			if (
				isGapComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isGapComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				fiGross_ += parseFloat(gapInsProfit_);
				fiGross_ += parseFloat(collisionTotProfit_);
			}
			if (
				isAftMktComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isAftMktComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				fiGross_ += parseFloat(aftmktProfit_);
			}

			if (
				isMiscComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isMiscComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				let miscProfit__ =
					totalSellerFees_ -
					miscCost_ -
					(lease.docfee ?? 0) -
					sale.dealerSmog -
					sale.stateSmog;
				fiGross_ += parseFloat(miscProfit__);
			}

			if (
				isBankFeeComm === commissionSettingsEnum.BACK_COMMISSION.value ||
				isBankFeeComm === commissionSettingsEnum.BOTH_COMMISION.value
			) {
				fiGross_ += parseFloat(recap.lenderFee);
			}
			//fiGross_ = twoDecimal(fiGross_);
			fiGross_ = roundUp(fiGross_);
			recap.editFiGross(fiGross_);
			return fiGross_;
		};

		const calcTotalGross = () => {
			let totalGross_ = 0;
			totalGross_ += carProfit_ + totalTradeProfit_ + interestNetProfit_;

			if (isDocFeeComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				totalGross_ += parseFloat(lease.docfee ?? 0);
			}
			if (isSmogComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				totalGross_ += parseFloat(sale.dealerSmog);
			}
			if (isReserveComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				totalGross_ += parseFloat(reserve_);
			}
			if (isServConComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				totalGross_ += parseFloat(warrantyProfit_);
			}
			if (isGapComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				totalGross_ += parseFloat(gapInsProfit_);
			}
			if (isAftMktComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				totalGross_ += parseFloat(aftmktProfit_);
			}
			if (isMiscComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				let miscProfit__ =
					totalSellerFees_ -
					miscCost_ -
					(lease.docfee ?? 0) -
					lease.bankFee -
					lease.securityDeposit -
					sale.dealerSmog -
					sale.stateSmog;

				totalGross_ += parseFloat(miscProfit__);
			}
			if (isBankFeeComm !== commissionSettingsEnum.NO_COMMISSION.value) {
				totalGross_ += parseFloat(recap.lenderFee);
			}

			totalGross_ += parseFloat(recap.additionalProfit);
			//totalGross_ = twoDecimal(totalGross_);
			totalGross_ = roundUp(totalGross_);
			recap.editTotalGross(totalGross_);
			return totalGross_;
		};

		const calculateCommission = () => {
			let commGross_ = 0;
			let salesComm_ = 0;
			let fiComm_ = 0;
			let totalComm_ = 0;
			let profitComm_ = 0;

			const newComm = deal.commissions.map((commission) => {
				const updatedCommission = { ...commission }; // Create a copy of the commission object

				if (Number(updatedCommission.commType) === commTypeList.SALE.value) {
					// updatedCommission.commAmount = twoDecimal(
					// 	salesGross_ * updatedCommission.commPercent +
					// 		updatedCommission.fixedAmount
					// );
					updatedCommission.commAmount = roundUp(
						salesGross_ * updatedCommission.commPercent +
							updatedCommission.fixedAmount
					);

					salesComm_ += updatedCommission.commAmount;
				} else if (
					Number(updatedCommission.commType) === commTypeList.FI.value
				) {
					// updatedCommission.commAmount = twoDecimal(
					// 	fiGross_ * updatedCommission.commPercent +
					// 		updatedCommission.fixedAmount
					// );
					updatedCommission.commAmount = roundUp(
						fiGross_ * updatedCommission.commPercent +
							updatedCommission.fixedAmount
					);
					fiComm_ += updatedCommission.commAmount;
				} else if (
					Number(updatedCommission.commType) === commTypeList.TOTAL.value
				) {
					// updatedCommission.commAmount = twoDecimal(
					// 	totalGross_ * updatedCommission.commPercent +
					// 		updatedCommission.fixedAmount
					// );
					updatedCommission.commAmount = roundUp(
						totalGross_ * updatedCommission.commPercent +
							updatedCommission.fixedAmount
					);
					totalComm_ += updatedCommission.commAmount;
				} else if (
					Number(updatedCommission.commType) === commTypeList.NETPROFIT.value
				) {
					// updatedCommission.commAmount = twoDecimal(
					// 	grossProfit_ * updatedCommission.commPercent +
					// 		updatedCommission.fixedAmount
					// );
					updatedCommission.commAmount = roundUp(
						grossProfit_ * updatedCommission.commPercent +
							updatedCommission.fixedAmount
					);
					profitComm_ += updatedCommission.commAmount;
				}
				commGross_ +=
					updatedCommission.bonus +
					updatedCommission.other +
					updatedCommission.commAmount;
				return updatedCommission;
			});

			deal.editCommissions(newComm);
			recap.editSalesComm(salesComm_);
			recap.editFiComm(fiComm_);
			recap.editTotalComm(totalComm_);
			recap.editProfitComm(profitComm_);
			recap.editCommGross(commGross_);
			recap.editNetProfit(grossProfit_ - commGross_);
			return commGross_;
		};

		const salesGross_ = calcSalesGross();
		const fiGross_ = calcFIGross();
		const totalGross_ = calcTotalGross();
		const commGross_ = calculateCommission();

		const calcNetProfit = () => {
			//const finalVal = twoDecimal(grossProfit_ - commGross_);
			const finalVal = roundUp(grossProfit_ - commGross_);
			recap.editNetProfit(finalVal);
		};
		calcNetProfit();

		///////////////////

		lease.editField("depreciation", depreciation);
		lease.editField("interest", rentCharge); // ensure key matches your store
		lease.editField("basePayment", basePayment);
		lease.editField("monthlyTax", monthlyTax);
		lease.editField("monthlyDepreciation", monthlyDepreciation);
		lease.editField("monthlyInterest", monthlyInterest);
		lease.editField("monthlyPmt", monthlyPmt);
		lease.editField("payment", monthlyPmt);
		lease.editField("firstPmtDate", firstPmtDate);
		lease.editField("lsNextDueDate", lsNextDueDate);
		lease.editField("totTax", totTax);
		lease.editField("totalTax", totalTax);
		lease.editField("taxAmtInLease", totalTax);
		lease.editField("capReduction", capReduction);
		lease.editField("driveOff", driveOff);
		lease.editField("totalBase", totBase);
		lease.editField("totalLease", totLease);
		lease.editField("totalRent", rentCharge);
		lease.editField("downTax", downTax);
		lease.editField("lsRebate", lease.lsRebate);
		lease.editField("capCost", capCost);
		lease.editField("grossCapCost", capCost);
		lease.editField("netCap", netCap);
		lease.editField("down", sale.down);
		lease.editField("gapWaiver", sale.gap);
		lease.editField("warranty", sale.warranty);
		lease.editField("leaseDate", sale.loanDate);
		lease.editField("deferredTax", deferredTax);
		//Update the sales Store
		sale.editAmtFinanced(depreciation);
		sale.editFinanceCharge(rentCharge);
		sale.editPayment(monthlyPmt);
		sale.editFinalPmt(monthlyPmt);
		sale.editApr(lease.apr);
		sale.editTerm(lease.term);
		sale.editDaysToPay(daysToPay_);
		sale.editFirstDueDate(firstPmtDate);
		sale.editFinalDueDate(finalDueDate);
		sale.editPrice(lease.price);
		sale.editTotalTaxable(totalTaxable_);
		sale.editSalesTax(totalTax);
		sale.editTax(totalTax + sale.vit + sale.businessTax + sale.fixedAmountTax);
		sale.editMonthlyTax(monthlyTax);
		sale.editManufacturerRebate(lease.lsRebate);
		sale.editLoan(totLease + driveOff);
		sale.editBankFee(lease.bankFee);
		sale.editLoanPeriod(loanPeriod_);
		if (sale.saleType === 6) {
			sale.editApr(0);
			sale.editFinanceCharge(0);
			lease.editField("apr", 0);
			lease.editField("moneyFactor", 0);
			lease.editField("MonthlyInterest", 0);
		}

		recap.editTotalDown(capReduction);

		let userTax6 = 0;
		let userTax5 = 0;
		let userTax4 = 0;
		let userTax3 = 0;
		let userTax2 = 0;
		let userTax1 = 0;
		let totTaxRateTMP = 0;
		if (deal.deal.countyRecNum > 0 && deal.county.isTaxSplit) {
			totTaxRateTMP =
				deal.county.userTaxRate6 +
				deal.county.userTaxRate5 +
				deal.county.userTaxRate4 +
				deal.county.userTaxRate3 +
				deal.county.userTaxRate2 +
				deal.county.userTaxRate1;
			if (deal.county.userTaxRate6 > 0)
				userTax6 = roundUp(
					(deal.county.userTaxRate6 / totTaxRateTMP) * totalTax,
					2
				);

			if (deal.county.userTaxRate5 > 0)
				userTax5 = roundUp(
					(deal.county.userTaxRate5 / totTaxRateTMP) * totalTax,
					2
				);

			if (deal.county.userTaxRate4 > 0)
				userTax4 = roundUp(
					(deal.county.userTaxRate4 / totTaxRateTMP) * totalTax,
					2
				);
			if (deal.county.userTaxRate3 > 0)
				userTax3 = roundUp(
					(deal.county.userTaxRate3 / totTaxRateTMP) * totalTax,
					2
				);
			if (deal.county.userTaxRate2 > 0)
				userTax2 = roundUp(
					(deal.county.userTaxRate2 / totTaxRateTMP) * totalTax,
					2
				);

			userTax1 =
				totalTax - (userTax6 + userTax5 + userTax4 + userTax3 + userTax2);
		}
		sale.editUserTax6(userTax6);
		sale.editUserTax5(userTax5);
		sale.editUserTax4(userTax4);
		sale.editUserTax3(userTax3);
		sale.editUserTax2(userTax2);
		sale.editUserTax1(userTax1);
	}, [
		deal.pickupPmts,
		lease.msrp,
		lease.price,
		lease.residualValue,
		lease.term,
		lease.daysToPay,
		lease.lsRebate,
		lease.moneyFactor,
		lease.bankFee,
		lease.docfee,
		lease.securityDeposit,
		lease.gpsService,
		lease.mileageCharge,
		lease.capSalesTax,
		lease.capDMVFee,
		lease.capBankFee,
		lease.capGAPWaiver,
		lease.capWarranty,
		lease.capDocFee,
		lease.capMiscFee,
		lease.capOtherfee,
		lease.capDispositionFee,
		lease.capSmogFees,
		lease.capFirstPmt,
		lease.manufacturerRebate,

		sale.saleType,
		sale.loanDate,
		sale.taxRate,
		sale.down,
		sale.discount,
		sale.dealerSmog,
		sale.stateSmog,
		sale.smogImpact,
		sale.titleFee,
		sale.filingFee,
		sale.smogExemption,
		sale.weightFee,
		sale.tireFee,
		sale.bpa,
		sale.licenseFee,
		sale.licensePct,
		sale.registrationFee,
		sale.registrationPct,
		sale.reserveFlag,
		sale.fixedAmountTax,
		deal.downPayments,

		// tax defaults
		dmvTaxRate,
		servContractTaxRate,
		aftMktTaxRate,
		gapTaxRate,
		docFeeTaxRate,
		dealerSmogTaxRate,
		vitRate,
		businessTaxRate,
		fetTaxRate,
		isFetTaxable,

		// insurance premiums
		bodyInjuryPremium,
		collisionPremium,
		compPremium,
		medicalPremium,
		otherPremium,
		propertyPremium,

		// flags/contract
		cancellationAccepted,

		// recap knobs
		recap.totalDown,
		recap.interestCost,
		recap.dlrParticipation,
		recap.additionalProfit,
		recap.gapInsCost,
		recap.collisionTotCost,
		recap.warrantyCost,
		lease.editField,
	]);

	const memoCalculateLeaseRecap = useCallback(() => {
		const calcOfficialFeesProfit = () => {
			const official_ = lease.totTax + sale.totalDMVFees;
			recap.editOfficial(official_);
		};
		// const official_ =
		calcOfficialFeesProfit();

		const calcGPBeforeFees = () => {
			const total =
				parseFloat(recap.frontendProfit) + parseFloat(recap.backendProfit);
			//const finalVal = twoDecimal(total);
			const finalVal = roundUp(total);
			recap.editGpBeforeFees(finalVal);
		};
		calcGPBeforeFees();
		const calcLenderFeeInfo = () => {
			recap.editTotalLenderFee(recap.lenderFee + recap.fiDiscount);
			recap.editNetCheckToDealer(
				lease.depreciation -
					(recap.lenderFee + recap.fiDiscount) +
					recap.interestNetProfit
			);
		};
		calcLenderFeeInfo();

		const calcGPBeforeReserve = () => {
			const total =
				parseFloat(recap.gpBeforeFees) -
				parseFloat(recap.totalLenderFee) +
				parseFloat(recap.incentive);
			//+				parseFloat(holdback);
			//const finalVal = twoDecimal(total);
			const finalVal = roundUp(total);
			recap.editGpBeforeReserve(finalVal);
		};
		calcGPBeforeReserve();

		const calcGPBeforeComm = () => {
			const total =
				parseFloat(recap.gpBeforeReserve) - parseFloat(recap.reserve);
			//const finalVal = twoDecimal(total);
			const finalVal = roundUp(total);
			recap.editGpBeforeComm(finalVal);
			recap.editGrossProfit(finalVal);
		};
		calcGPBeforeComm();

		const calcReceivable = () => {
			const total =
				parseFloat(recap.depreciation) -
				(parseFloat(recap.lenderFee) + parseFloat(recap.fiDiscount)) +
				parseFloat(recap.interestNetProfit) +
				parseFloat(recap.totalDown);
			//const finalVal = twoDecimal(total);
			const finalVal = roundUp(total);
			recap.editReceivable(finalVal);
		};
		calcReceivable();

		const calcReserve = () => {
			const resAmt = lease.depreciation * recap.reservePercent;
			recap.editReserve(resAmt);
		};

		calcReserve();
	}, [
		sale.salesTax,
		sale.totalDMVFees,

		lease.depreciation,

		recap.frontendProfit,
		recap.backendProfit,
		recap.lenderFee,
		recap.fiDiscount,
		recap.interestNetProfit,
		recap.incentive,
		recap.reserve,
		recap.reservePercent,
		recap.totalDown,
	]);
	useEffect(() => {
		console.log("Beginning Sale Calculation...");
		if (isLease) {
			if (type !== DealStatus.PENDING) {
				console.log("not pending calcs...");
				memoCalculateLeaseRecap();
			} else {
				console.log("pending calcs...");
				memoCalculateLease();
			}
		}
	}, [
		isLease,
		type, // DealStatus
		// the handful of inputs that gate recompute at a high level
		lease.price,
		lease.residualValue,
		lease.term,
		lease.daysToPay,
		lease.lsRebate,
		lease.moneyFactor,
		sale.loanDate,
		sale.down,
		sale.taxRate,
		recap.totalDown,
		recap.interestCost,
		recap.dlrParticipation,
		recap.additionalProfit,
		lease.bankFee,
		lease.capSalesTax,
		lease.capDMVFee,
		lease.capBankFee,
		lease.capGAPWaiver,
		lease.capWarranty,
		lease.capDocFee,
		lease.capMiscFee,
		lease.capOtherfee,
		lease.capDispositionFee,
		lease.capSmogFees,
		lease.capFirstPmt,
		lease.gpsService,
		lease.manufacturerRebate,
		// and the memoized callbacks
		memoCalculateLease,
		memoCalculateLeaseRecap,
	]);
};

export default useCalculateLease;
